import { ApiCall } from '@dto/api-calls.dto';
import { CampaignByProductIdDto } from '@dto/campaign/campaign-by-product-id.dto';
import { createReducer, on } from '@ngrx/store';
import { initCampaignByProductIds, initCampaignByProductIdsFailure, initCampaignByProductIdsSuccess, sendCampaign, sendCampaignFailure, sendCampaignSuccess, changeCampaignListFilters, saveCampaignFailure, saveCampaignSuccess, saveCampaign, scheduleCampaign, scheduleCampaignSuccess, scheduleCampaignFailure, deleteCampaign, deleteCampaignSuccess, deleteCampaignFailure } from './campaign.action';
import { TeamsCampaignFilter } from '@entity/teams-campaign-filter.entity';
import { CampaignDto, DraftCampaignDto, ScheduledCampaignDto } from '@dto/campaign/campaign.dto';
import { NullableType } from '@type/nullable.type';


export interface CampaignState {
  campaignByProductId: ApiCall<CampaignByProductIdDto>;
  campaignCreation: ApiCall<NullableType<CampaignDto>>;
  campaignSave: ApiCall<NullableType<DraftCampaignDto>>;
  campaignScheduling: ApiCall<NullableType<ScheduledCampaignDto>>;
  campaignDeleting: ApiCall<NullableType<string>>;
  campaignListSelectedFilters: TeamsCampaignFilter;
}

export const initialState: CampaignState = {
  campaignByProductId: { data: {}, isLoading: false, error: null, hasBeenCalled: false },
  campaignCreation: { data: null, isLoading: false, error: null },
  campaignSave: { data: null, isLoading: false, error: null },
  campaignScheduling: { data: null, isLoading: false, error: null },
  campaignDeleting: { data: null, isLoading: false, error: null },
  campaignListSelectedFilters: {
    campaignName: '',
    productIds: [],
    period: null,
    campaignStates: []
  }
};

export const campaignReducer = createReducer(
  initialState,

  // Campaign Names
  on(initCampaignByProductIds, (state) => ({
    ...state,
    campaignByProductId: {
      ...state.campaignByProductId,
      isLoading: true,
      error: null,
    },
  })),
  on(initCampaignByProductIdsSuccess, (state, { campaignByProductId }) => ({
    ...state,
    campaignByProductId: {
      ...state.campaignByProductId,
      data: campaignByProductId,
      isLoading: false,
      hasBeenCalled: true,
    },
  })),
  on(initCampaignByProductIdsFailure, (state, { error }) => ({
    ...state,
    campaignByProductId: {
      ...state.campaignByProductId,
      isLoading: false,
      error: error,
      hasBeenCalled: true,
    },
  })),

  // Campaign Creation
  on(sendCampaign, (state) => ({
    ...state,
    campaignCreation: {
      ...state.campaignCreation,
      data: null,
      isLoading: true,
      error: null,
    },
  })),
  on(sendCampaignSuccess, (state, { campaign }) => ({
    ...state,
    campaignCreation: {
      ...state.campaignCreation,
      data: campaign,
      isLoading: false,
    },
  })),
  on(sendCampaignFailure, (state, { error }) => ({
    ...state,
    campaignCreation: {
      ...state.campaignCreation,
      isLoading: false,
      error: error,
    },
  })),

  on(saveCampaign, (state) => ({
    ...state,
    campaignSave: {
      ...state.campaignSave,
      data: null,
      isLoading: true,
      error: null,
    },
  })),
  on(saveCampaignSuccess, (state, { campaign }) => ({
    ...state,
    campaignSave: {
      ...state.campaignSave,
      data: campaign,
      isLoading: false,
    },
  })),
  on(saveCampaignFailure, (state, { error }) => ({
    ...state,
    campaignSave: {
      ...state.campaignSave,
      isLoading: false,
      error: error,
    },
  })),

  on(scheduleCampaign, (state) => ({
    ...state,
    campaignScheduling: {
      ...state.campaignScheduling,
      data: null,
      isLoading: true,
      error: null,
    },
  })),
  on(scheduleCampaignSuccess, (state, { campaign }) => ({
    ...state,
    campaignScheduling: {
      ...state.campaignScheduling,
      data: campaign,
      isLoading: false,
    },
  })),
  on(scheduleCampaignFailure, (state, { error }) => ({
    ...state,
    campaignScheduling: {
      ...state.campaignScheduling,
      isLoading: false,
      error: error,
    },
  })),

  on(deleteCampaign, (state) => ({
    ...state,
    campaignDeleting: {
      ...state.campaignDeleting,
      data: null,
      isLoading: true,
      error: null,
    },
  })),
  on(deleteCampaignSuccess, (state, { campaignId }) => ({
    ...state,
    campaignDeleting: {
      ...state.campaignDeleting,
      data: campaignId,
      isLoading: false,
    },
    campaignByProductId: {
      ...state.campaignByProductId,
      data: Object.entries(state.campaignByProductId.data).reduce((acc, [productId, campaigns]) => ({
          ...acc,
          [productId]: campaigns.filter(campaign => campaign.id !== campaignId)
        }), {}),
    },
  })),
  on(deleteCampaignFailure, (state, { error }) => ({
    ...state,
    campaignDeleting: {
      ...state.campaignDeleting,
      isLoading: false,
      error: error,
    },
  })),

  // Campaigns list filters
  on(changeCampaignListFilters, (state, { filters }) => ({
    ...state,
    campaignListSelectedFilters: filters,
  })),
);
